import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ListChecks } from 'lucide-react';
import clsx from 'clsx';
import type { ReportSettings, ReportTemplateDto } from '@bbl/validation';
import { apiRequest, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { useMenuPath } from '../lib/menu-path';
import { ListShell } from '../components/ListShell';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { SkeletonCard } from '../components/ui/Skeleton';
import { GatedSection } from '../components/shared/GatedSection';
import { WindowsSettingsForm } from '../components/teacher-reports/admin/WindowsSettingsForm';
import { PointsSettingsForm } from '../components/teacher-reports/admin/PointsSettingsForm';
import { TemplatesOverview } from '../components/teacher-reports/admin/TemplatesOverview';
import { VoiceNoteSettings } from '../components/teacher-reports/admin/VoiceNoteSettings';

export const Route = createFileRoute('/_authed/system/teacher-reports')({
  component: TeacherReportSettingsPage,
});

type Tab = 'windows' | 'checklists' | 'points';
const TABS: ReadonlyArray<{ value: Tab; label: string }> = [
  { value: 'windows', label: 'When reports open' },
  { value: 'checklists', label: 'Checklists' },
  { value: 'points', label: 'Points & chosen teachers' },
];

function TeacherReportSettingsPage() {
  const canView = useAuthStore((s) => s.hasRole('super_admin', 'academic_admin'));
  const canEdit = useAuthStore((s) => s.user?.primaryRole === 'super_admin');
  const featuresPath = useMenuPath('/system/feature-flags');
  const [tab, setTab] = useState<Tab>('windows');

  const settingsQuery = useQuery({
    queryKey: ['teacher-reports', 'settings'] as const,
    enabled: canView,
    queryFn: ({ signal }) => apiRequest<ReportSettings & { version: number }>('/api/teacher-reports/settings', { signal }),
    retry: (n, err) => !(err instanceof ApiError && err.status === 404) && n < 2,
  });
  const templatesQuery = useQuery({
    queryKey: ['teacher-reports', 'templates'] as const,
    enabled: canView,
    queryFn: ({ signal }) => apiRequest<{ items: ReportTemplateDto[] }>('/api/teacher-reports/templates', { signal }),
    retry: (n, err) => !(err instanceof ApiError && err.status === 404) && n < 2,
  });

  if (!canView) {
    return (
      <ListShell title="Report settings">
        <EmptyState
          title="Admins only"
          description="Report windows, checklists and points are managed by the academy admin."
          icon={<ListChecks size={32} aria-hidden="true" />}
        />
      </ListShell>
    );
  }

  return (
    <ListShell
      title="Report settings"
      description="When daily, weekly and monthly reports open and close, what each checklist asks, and how points turn into incentives."
    >
      <GatedSection
        error={settingsQuery.error}
        title="Teacher reports"
        description={
          featuresPath ? `Switch on Teacher reports in ${featuresPath} to set it up.` : 'Ask a super admin to switch on Teacher reports.'
        }
        onRetry={() => void settingsQuery.refetch()}
      >
        {settingsQuery.isLoading || !settingsQuery.data ? (
          <div className="grid gap-3">
            <SkeletonCard />
            <SkeletonCard />
          </div>
        ) : (
          <>
            <div role="tablist" aria-label="Settings sections" className="flex gap-1 overflow-x-auto border-b border-[color:var(--color-border)]">
              {TABS.map((t) => (
                <button
                  key={t.value}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.value}
                  onClick={() => setTab(t.value)}
                  className={clsx(
                    '-mb-px whitespace-nowrap border-b-2 px-3 py-2 text-sm',
                    tab === t.value
                      ? 'border-gold-500 font-semibold text-[color:var(--color-text)]'
                      : 'border-transparent text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]',
                  )}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div role="tabpanel">
              {tab === 'windows' ? <WindowsSettingsForm settings={settingsQuery.data} canEdit={canEdit} /> : null}
              {tab === 'checklists' ? (
                <>
                  {templatesQuery.isLoading ? (
                    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                      <SkeletonCard />
                      <SkeletonCard />
                      <SkeletonCard />
                    </div>
                  ) : templatesQuery.isError || !templatesQuery.data ? (
                    <ErrorState
                      title="Could not load the checklists"
                      description={templatesQuery.error instanceof ApiError ? templatesQuery.error.message : undefined}
                      onRetry={() => void templatesQuery.refetch()}
                    />
                  ) : (
                    <TemplatesOverview templates={templatesQuery.data.items} canEdit={canEdit} />
                  )}
                  <VoiceNoteSettings settings={settingsQuery.data} canEdit={canEdit} />
                </>
              ) : null}
              {tab === 'points' ? <PointsSettingsForm settings={settingsQuery.data} canEdit={canEdit} /> : null}
            </div>
          </>
        )}
      </GatedSection>
    </ListShell>
  );
}
